import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import NumberInput from 'src/components/NumberInput';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import PopUp from 'src/components/design-system/PopUp';
import useBoolean from 'src/hooks/useBoolean';
import usePeriodView from 'src/hooks/usePeriodView';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { budgetPercentageCal } from 'src/utils';
import { round } from 'src/utils/number';

const AdjustGLCodeBudget = () => {
  const { setEditingGlCodePeriodBudget, t, editingbudgetPeriodDataWithErrors } = useViewCheckbook();
  const {
    adjustBudgetPercentage,
    setAdjustBalancePercentage,
    updateAdjustBalancePercentageErrorMessage,
    adjustBalancePercentageErrors,
  } = usePeriodView();
  const { value: adjustBudgetModal, toggle: toggleAdjustBudgetModal } = useBoolean();

  const handleCalculateBudget = useCallback(() => {
    let isSuccess = true;
    setEditingGlCodePeriodBudget((prev) => {
      const adjustGLCode = [...prev].map((glCode) => {
        if (glCode.selected && !glCode.isFixed && glCode.budgetAmount) {
          const newBudgetAmount = round(
            (Number(adjustBudgetPercentage) / 100) * glCode.budgetAmount,
            2
          );
          const newValue = Number(newBudgetAmount);
          const remainingBudgetAmount =
            newValue - (glCode.spentAmount || 0) - (glCode.holdAmount || 0);

          const remainingBudgetAmountPercentage = budgetPercentageCal(
            remainingBudgetAmount,
            newValue
          );

          if (!Number.isFinite(newBudgetAmount)) {
            isSuccess = false;
            return glCode;
          }
          return {
            ...glCode,
            budgetAmount: newBudgetAmount,
            remainingBudgetAmountPercentage: remainingBudgetAmountPercentage,
            remainingBudgetAmount: remainingBudgetAmount,
          };
        }
        return glCode;
      });
      return adjustGLCode;
    });
    toggleAdjustBudgetModal();

    if (isSuccess) {
      toast.success("Successfully updated selected code's forecast amounts");
    } else {
      toast.error('Error updating forecast amounts by %');
    }
    setAdjustBalancePercentage('100');
  }, [
    adjustBudgetPercentage,
    setAdjustBalancePercentage,
    setEditingGlCodePeriodBudget,
    toggleAdjustBudgetModal,
  ]);

  const { isAdjustBudgetDisable, message } = useMemo(() => {
    let isAdjustBudgetDisable = true;
    let message: string[] = [];
    const selectedGLCode = editingbudgetPeriodDataWithErrors.filter((glCode) => glCode.selected);
    const isFixedGLCode = selectedGLCode.some((glCode) => glCode.isFixed);
    const isDeleted = selectedGLCode.some((glCode) => glCode.isDeleted);

    if (selectedGLCode.length === 0) {
      message = ['Please select one or more records to adjust by %.'];
      isAdjustBudgetDisable = true;
    } else if (isFixedGLCode || isDeleted) {
      if (isFixedGLCode) {
        message = [
          'One or more of the selected records is fixed. Only variable (non-fixed) codes can be adjusted by %.',
        ];
      }
      if (isDeleted) {
        message = ['One or more of the selected records is deleted.'];
      }
      isAdjustBudgetDisable = true;
    } else {
      isAdjustBudgetDisable = false;
      message = [];
    }
    return {
      isAdjustBudgetDisable,
      message,
    };
  }, [editingbudgetPeriodDataWithErrors]);

  const handleAdjustBalancePercentage = useCallback(
    (e: any) => {
      const inputValue = e.value;
      setAdjustBalancePercentage(inputValue);
    },
    [setAdjustBalancePercentage]
  );

  const isCalculateButtonDisabled = useMemo(
    () => adjustBalancePercentageErrors.isFieldEmpty || adjustBalancePercentageErrors.isZero,
    [adjustBalancePercentageErrors.isFieldEmpty, adjustBalancePercentageErrors.isZero]
  );
  return (
    <>
      <PopUp
        content={
          <div className="m-1 max-w-[350px]">
            <PopUpContentWrapper type="Information" contents={message as string[]} />
          </div>
        }
        show={isAdjustBudgetDisable}
      >
        <Button onClick={toggleAdjustBudgetModal} disabled={isAdjustBudgetDisable} color="info">
          {t('Adjust Forecast By %')}
        </Button>
      </PopUp>
      <Dialog
        title={'Adjust the variable GL Forecast Amounts'}
        open={adjustBudgetModal}
        onClose={() => {
          toggleAdjustBudgetModal();
          setAdjustBalancePercentage('100');
        }}
        id="modal"
        width={'600px'}
      >
        <div className="text-[13px]">
          <div className="mb-2 inline-block">
            <span>Adjust the variable GL Forecast Amounts to</span>
            <span className="w-[70px] mx-1">
              <NumberInput
                value={adjustBudgetPercentage}
                onChange={handleAdjustBalancePercentage}
                decimal={0}
                width={70}
                valid={!isCalculateButtonDisabled}
              />
            </span>
            <span>% of the amount currently displayed</span>
          </div>
          <p className="bg-[#D9AA4F33] p-2 rounded-sm">
            Note: Selected Code's Forecast Amounts will first be updated in View Checkbook for
            review. Once saved, you will not be able to restore the previous values.
          </p>
        </div>
        <DialogActionsBar>
          <div className="flex justify-end items-center">
            <PopUp
              popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
              anchorAlign={{ horizontal: 'right', vertical: 'top' }}
              className="max-h-[180px] "
              content={
                <div className="m-1 max-w-[350px]">
                  <PopUpContentWrapper
                    type="Error"
                    contents={updateAdjustBalancePercentageErrorMessage}
                  />
                </div>
              }
              show={isCalculateButtonDisabled}
            >
              <Button
                onClick={handleCalculateBudget}
                color="primary"
                disabled={isCalculateButtonDisabled}
              >
                Calculate
              </Button>
            </PopUp>
            <Button
              onClick={() => {
                toggleAdjustBudgetModal();
                setAdjustBalancePercentage('100');
              }}
              color="info"
            >
              Cancel
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </>
  );
};

export default AdjustGLCodeBudget;
